const META_PIXEL_ID = process.env.GATSBY_META_PIXEL_ID;
const CONSENT_COOKIE_NAME = "CookieConsent";

export const hasCookieConsent = (): boolean => {
  if (typeof document === "undefined") {
    return false;
  }
  return document.cookie
    .split("; ")
    .some(c => c.startsWith(`${CONSENT_COOKIE_NAME}=true`));
};

export const initMetaPixel = (): void => {
  if (
    typeof window === "undefined" ||
    META_PIXEL_ID === undefined ||
    META_PIXEL_ID === "" ||
    window.fbq !== undefined
  ) {
    return;
  }

  const script = document.createElement("script");
  script.async = true;
  script.innerHTML = `
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${META_PIXEL_ID}');
fbq('track', 'PageView');
  `.trim();
  document.head.appendChild(script);
};

export const trackMetaPixelEvent = (
  event: string,
  params?: Record<string, unknown>,
  eventId?: string,
): void => {
  if (window?.fbq === undefined) {
    return;
  }
  // eventId enables Meta event de-duplication (safe against effect re-fires, and
  // lets a future server-side Conversions API event dedupe against this one).
  if (eventId !== undefined && eventId !== "") {
    window.fbq("track", event, params, { eventID: eventId });
  } else {
    window.fbq("track", event, params);
  }
};

// --- Typed helpers for the standard Meta events, mirroring the existing gtag
// (Google Analytics) events already fired across the funnel. Each no-ops until
// the pixel is initialised (i.e. after cookie consent). ---
type MetaContentId = string | number;

interface MetaProductEventParams {
  value?: number;
  currency?: string;
  contentIds?: Array<MetaContentId | undefined | null>;
  contentName?: string;
  eventId?: string;
}

const buildProductParams = (
  p: MetaProductEventParams,
): Record<string, unknown> => {
  const params: Record<string, unknown> = { content_type: "product" };
  if (p.value !== undefined && !Number.isNaN(p.value)) {
    params.value = p.value;
  }
  if (p.currency !== undefined && p.currency !== "") {
    params.currency = p.currency;
  }
  if (p.contentName !== undefined && p.contentName !== "") {
    params.content_name = p.contentName;
  }
  const ids = (p.contentIds ?? []).filter(
    (id): id is MetaContentId => id !== undefined && id !== null && id !== "",
  );
  if (ids.length > 0) {
    params.content_ids = ids.map(id => String(id));
  }
  return params;
};

export const trackMetaPurchase = (p: MetaProductEventParams): void => {
  trackMetaPixelEvent("Purchase", buildProductParams(p), p.eventId);
};

export const trackMetaInitiateCheckout = (p: MetaProductEventParams): void => {
  trackMetaPixelEvent("InitiateCheckout", buildProductParams(p), p.eventId);
};

export const trackMetaViewContent = (p: MetaProductEventParams): void => {
  trackMetaPixelEvent("ViewContent", buildProductParams(p), p.eventId);
};

export const trackMetaAddToCart = (p: MetaProductEventParams): void => {
  trackMetaPixelEvent("AddToCart", buildProductParams(p), p.eventId);
};

export const trackMetaLead = (params: Record<string, unknown> = {}): void => {
  trackMetaPixelEvent("Lead", params);
};

export const trackMetaCompleteRegistration = (
  params: Record<string, unknown> = {},
): void => {
  trackMetaPixelEvent("CompleteRegistration", params);
};

export const trackMetaSearch = (searchString: string): void => {
  if (searchString === "") {
    return;
  }
  trackMetaPixelEvent("Search", { search_string: searchString });
};
